import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { UsersThree, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { engApi } from './engApi';

export default function EngMembers() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    engApi.members()
      .then((r) => { setUsers(r.users || []); setSelected(r.member_user_ids || []); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'メンバーの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const nq = q.trim().toLowerCase();
    if (!nq) return users;
    return users.filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(nq));
  }, [users, q]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    setSaving(true);
    try {
      await engApi.setMembers(selected);
      toast.success('保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-sm text-zinc-400">読込中…</div>;

  return (
    <div className="space-y-5" data-testid="eng-members-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <UsersThree size={24} weight="duotone" className="text-teal-600" /> 技術メンバー
          </h1>
          <p className="text-sm text-zinc-500 mt-1">メンバーは資産の登録・使用・スクラップなど全ての操作ができます（管理者は常に操作可能）</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="eng-members-save">
          {saving ? '保存中…' : `保存（${selected.length}名）`}
        </Button>
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="名前・メールで検索…" className="h-10 pl-8" data-testid="eng-members-search" />
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 divide-y divide-zinc-200 shadow-sm" data-testid="eng-members-list">
        {filtered.map((u) => (
          <label key={u.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-zinc-50" data-testid={`eng-member-row-${u.id}`}>
            <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`eng-member-check-${u.id}`} />
            <div className="min-w-0">
              <p className="text-sm font-medium text-zinc-900 truncate">{u.name || u.email}</p>
              <p className="text-[11px] text-zinc-400 truncate">{u.email}{u.role === 'admin' ? '（管理者）' : ''}</p>
            </div>
          </label>
        ))}
        {filtered.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-400">該当するユーザーがいません</div>}
      </div>
    </div>
  );
}
